import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ShieldCheck, BadgeCheck, MapPin } from "lucide-react";
import { SITE } from "../config/site";

const HERO_IMG =
  "https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxicmlnaHQlMjBjbGVhbiUyMGxpdmluZyUyMHJvb218ZW58MHx8fHwxNzg2NDE0NzQ1fDA&ixlib=rb-4.1.0&q=80&w=1920&auto=format";

const line = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] },
  }),
};

const badges = [
  { icon: ShieldCheck, label: "Insured" },
  { icon: BadgeCheck, label: "Bonded" },
  { icon: MapPin, label: "Locally Owned" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative flex min-h-[100svh] items-end overflow-hidden bg-salt">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="A bright, freshly cleaned living room"
          className="h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-salt via-salt/55 to-salt/15" />
      </motion.div>

      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-20 pt-40 lg:px-12 lg:pb-28">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="mb-6 text-xs font-semibold uppercase tracking-[0.35em] text-ocean"
          data-testid="hero-eyebrow"
        >
          {SITE.region} — Insured · Bonded · Locally Owned
        </motion.p>

        <h1 className="font-serif text-ink" data-testid="hero-headline">
          <span className="headline-mask">
            <motion.span
              custom={0}
              variants={line}
              initial="hidden"
              animate="visible"
              className="block text-5xl leading-[1.02] tracking-tight sm:text-7xl lg:text-[7rem]"
            >
              Clean spaces,
            </motion.span>
          </span>
          <span className="headline-mask">
            <motion.span
              custom={1}
              variants={line}
              initial="hidden"
              animate="visible"
              className="block text-5xl italic leading-[1.02] tracking-tight text-ocean sm:text-7xl lg:text-[7rem]"
            >
              better places.
            </motion.span>
          </span>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-8 max-w-xl text-base leading-relaxed text-slateblue md:text-lg"
          data-testid="hero-subtext"
        >
          Reliable, detail-oriented cleaning for homes and businesses across the Avalon.
          Every client gets our full attention — not a rushed clean. Competitive pricing.
          Satisfaction guaranteed.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#quote"
            data-testid="hero-quote-button"
            className="rounded-full bg-seafoam px-8 py-4 text-sm font-bold uppercase tracking-widest text-white transition-[background-color,transform] duration-300 hover:bg-ocean hover:scale-[1.03]"
          >
            Get a Free Quote
          </a>
          <a
            href="#services"
            data-testid="hero-services-button"
            className="rounded-full border border-ink/25 bg-white/60 px-8 py-4 text-sm font-bold uppercase tracking-widest text-ink backdrop-blur-sm transition-[background-color,border-color] duration-300 hover:border-ink hover:bg-white"
          >
            Explore Services
          </a>
        </motion.div>

        <motion.ul
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3, duration: 1 }}
          className="mt-14 flex flex-wrap gap-x-10 gap-y-3"
          data-testid="hero-trust-badges"
        >
          {badges.map(({ icon: Icon, label }) => (
            <li key={label} className="flex items-center gap-2 text-sm font-medium text-slateblue">
              <Icon className="h-4 w-4 text-seafoam" strokeWidth={2.2} />
              {label}
            </li>
          ))}
        </motion.ul>
      </div>
    </section>
  );
}
